import { useCallback, useEffect, useState } from 'react';
import {
  HandCoins, Loader2, AlertCircle, Search, X, Trash2, RefreshCw,
  Vault, UserPlus, Pencil, Ban, Tag,
} from 'lucide-react';
import {
  getRmtConfig, updateRmtConfig, setRmtCofre, searchRmtChar, runRmtScan,
  listRmtSellers, searchRmtAccount, grantRmtSeller, updateRmtSeller, revokeRmtSeller,
  getRmtBlacklist, addRmtBlacklist, removeRmtBlacklist,
  getRmtPriceLimits, upsertRmtPriceLimit, removeRmtPriceLimit,
  type RmtConfig, type RmtSeller, type RmtAccountSearch, type RmtCharSearch,
  type RmtSellLimits, type RmtItemConfig, type RmtBlacklistItem, type RmtPriceLimit,
} from '@/shared/services/rmtService';
import { useToast } from '@/shared/contexts/ToastContext';

const fmt = (n: number) => (n ?? 0).toLocaleString('pt-BR');

const STATUS_BADGE: Record<string, string> = {
  active: 'bg-emerald-500/15 text-emerald-400',
  suspended: 'bg-red-500/15 text-red-400',
};
const DOC_BADGE: Record<string, string> = {
  pending: 'bg-amber-500/15 text-amber-400',
  approved: 'bg-emerald-500/15 text-emerald-400',
  rejected: 'bg-red-500/15 text-red-400',
};
const DOC_LABEL: Record<string, string> = {
  pending: 'Doc. pendente', approved: 'Doc. aprovada', rejected: 'Doc. rejeitada',
};

function LimitField({ label, value, onChange }: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div>
      <label className="text-[11px] text-white/55 block mb-1">{label}</label>
      <input
        type="number"
        min={0}
        value={value}
        onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))}
        className="w-full bg-[#0a1015] border border-white/[0.06] rounded-lg
                   px-3 py-2 text-sm text-white"
      />
    </div>
  );
}


export default function RmtPage() {
  const { notify } = useToast();
  const [config, setConfig] = useState<RmtConfig | null>(null);
  const [sellers, setSellers] = useState<RmtSeller[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [depositEnabled, setDepositEnabled] = useState(false);
  const [minDeposit, setMinDeposit] = useState(0);
  const [limits, setLimits] = useState<RmtSellLimits>({
    limitAlzDay: 0, limitAlzWeek: 0, limitAlzMonth: 0,
    limitSalesDay: 0, limitSalesWeek: 0, limitSalesMonth: 0,
  });
  const [itemCfg, setItemCfg] = useState<RmtItemConfig>({
    itemsEnabled: false, alzPriceMinBrl: 0, alzPriceMaxBrl: 0,
    itemPriceMinBrl: 0, itemPriceMaxBrl: 0,
  });
  const [savingCfg, setSavingCfg] = useState(false);
  const [scanning, setScanning] = useState(false);

  const [blacklist, setBlacklist] = useState<RmtBlacklistItem[]>([]);
  const [priceLimits, setPriceLimits] = useState<RmtPriceLimit[]>([]);
  const [blItemId, setBlItemId] = useState('');
  const [blOption, setBlOption] = useState('');
  const [blReason, setBlReason] = useState('');
  const [plItemId, setPlItemId] = useState('');
  const [plMin, setPlMin] = useState('');
  const [plMax, setPlMax] = useState('');

  const [cofreOpen, setCofreOpen] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<RmtAccountSearch[]>([]);

  const [editing, setEditing] = useState<RmtSeller | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cfg, sel, bl, pl] = await Promise.all([
        getRmtConfig(), listRmtSellers(), getRmtBlacklist(), getRmtPriceLimits(),
      ]);
      setConfig(cfg);
      setDepositEnabled(cfg.depositEnabled);
      setMinDeposit(cfg.minDepositAlz);
      setLimits({
        limitAlzDay: cfg.limitAlzDay, limitAlzWeek: cfg.limitAlzWeek,
        limitAlzMonth: cfg.limitAlzMonth, limitSalesDay: cfg.limitSalesDay,
        limitSalesWeek: cfg.limitSalesWeek, limitSalesMonth: cfg.limitSalesMonth,
      });
      setItemCfg({
        itemsEnabled: cfg.itemsEnabled,
        alzPriceMinBrl: cfg.alzPriceMinBrl, alzPriceMaxBrl: cfg.alzPriceMaxBrl,
        itemPriceMinBrl: cfg.itemPriceMinBrl, itemPriceMaxBrl: cfg.itemPriceMaxBrl,
      });
      setBlacklist(bl);
      setPriceLimits(pl);
      setSellers(sel);
    } catch (err: any) {
      setError(err?.detail || err?.message || 'Erro carregando RMT');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveConfig = async () => {
    setSavingCfg(true);
    try {
      const cfg = await updateRmtConfig({
        depositEnabled, minDepositAlz: minDeposit, ...limits, ...itemCfg,
      });
      setConfig(cfg);
      setDepositEnabled(cfg.depositEnabled);
      notify('success', 'Configuração salva');
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
      setDepositEnabled(config?.depositEnabled ?? false);
    } finally {
      setSavingCfg(false);
    }
  };

  const addBl = async () => {
    const id = Number(blItemId);
    if (!id) { notify('error', 'Informe o ID do item'); return; }
    try {
      await addRmtBlacklist(id, blOption.trim() ? Number(blOption) : null, blReason.trim());
      notify('success', 'Item adicionado à blacklist');
      setBlItemId(''); setBlOption(''); setBlReason('');
      setBlacklist(await getRmtBlacklist());
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    }
  };

  const delBl = async (id: number) => {
    try {
      await removeRmtBlacklist(id);
      setBlacklist(await getRmtBlacklist());
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    }
  };

  const savePl = async () => {
    const id = Number(plItemId);
    if (!id) { notify('error', 'Informe o ID do item'); return; }
    try {
      await upsertRmtPriceLimit(id, {
        minPriceBrl: Number(plMin) || 0, maxPriceBrl: Number(plMax) || 0,
      });
      notify('success', 'Limite de preço salvo');
      setPlItemId(''); setPlMin(''); setPlMax('');
      setPriceLimits(await getRmtPriceLimits());
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    }
  };

  const delPl = async (itemId: number) => {
    try {
      await removeRmtPriceLimit(itemId);
      setPriceLimits(await getRmtPriceLimits());
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    }
  };

  const doScan = async () => {
    setScanning(true);
    try {
      const r = await runRmtScan();
      if (!r.enabled) notify('warning', 'Detecção desativada ou cofre não definido');
      else notify('success', `Scan: ${r.processed} depósito(s) · ${fmt(r.totalAlz)} alz`);
      await fetchAll();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro no scan');
    } finally {
      setScanning(false);
    }
  };

  const doSearch = async () => {
    const q = searchTerm.trim();
    if (q.length < 2) { notify('error', 'Digite ao menos 2 caracteres'); return; }
    setSearching(true);
    try { setSearchResults(await searchRmtAccount(q)); }
    catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
    finally { setSearching(false); }
  };

  const onGrant = async (r: RmtAccountSearch) => {
    try {
      await grantRmtSeller(r.userNum);
      notify('success', `${r.accountId} habilitado como vendedor`);
      await fetchAll();
      await doSearch();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    }
  };

  const onRevoke = async (s: RmtSeller) => {
    if (!confirm(`Revogar acesso RMT de ${s.accountId || `UserNum ${s.userNum}`}?`)) return;
    try {
      await revokeRmtSeller(s.userNum);
      notify('success', 'Acesso revogado');
      await fetchAll();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
            <HandCoins className="text-amber-400" size={26} />
            Marketplace RMT
          </h1>
          <p className="text-sm text-white/65 mt-1">
            {sellers.length} vendedor{sellers.length !== 1 && 'es'} · custódia de alz por escrow
          </p>
        </div>
        <button
          onClick={doScan}
          disabled={scanning}
          className="px-4 py-2 rounded-lg bg-white/[0.04] hover:bg-white/[0.08]
                     text-white/80 text-sm font-bold transition cursor-pointer
                     disabled:opacity-50 flex items-center gap-2 shrink-0"
        >
          {scanning ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Escanear depósitos
        </button>
      </div>

      {error && (
        <div className="surface-card rounded-xl p-4 mb-4 flex items-start gap-3 border-red-500/30">
          <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="py-16 flex items-center justify-center text-white/40">
          <Loader2 size={20} className="animate-spin" />
        </div>
      ) : (
        <>
          {/* Configuração */}
          <div className="surface-card rounded-xl p-5 mb-6">
            <p className="text-xs font-black uppercase tracking-[0.15em] text-white/55 mb-4">
              Configuração de depósito
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Char-cofre */}
              <div className="bg-[#0d141b] border border-white/[0.06] rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Vault size={15} className="text-amber-400" />
                  <span className="text-xs font-bold text-white/70 uppercase tracking-wider">
                    Char-cofre
                  </span>
                </div>
                {config?.cofreCharName ? (
                  <p className="text-sm text-white font-bold">
                    {config.cofreCharName}
                    <span className="text-white/45 font-mono font-normal">
                      {' '}· idx {config.cofreCharIdx}
                    </span>
                  </p>
                ) : (
                  <p className="text-sm text-red-400">Não definido</p>
                )}
                <p className="text-[11px] text-white/45 mt-1">
                  Personagem que recebe os depósitos por correio.
                </p>
                <button
                  onClick={() => setCofreOpen(true)}
                  className="mt-3 px-3 py-1.5 rounded-lg bg-amber-500/10 hover:bg-amber-500/20
                             text-amber-400 text-xs font-bold transition cursor-pointer"
                >
                  {config?.cofreCharName ? 'Trocar char-cofre' : 'Definir char-cofre'}
                </button>
              </div>

              {/* Flags */}
              <div className="bg-[#0d141b] border border-white/[0.06] rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-bold text-white">Detecção de depósito</p>
                    <p className="text-[11px] text-white/45">
                      Liga o scan automático (a cada 60s).
                    </p>
                  </div>
                  <button
                    onClick={() => setDepositEnabled(v => !v)}
                    className={`relative w-11 h-6 rounded-full transition shrink-0
                                ${depositEnabled ? 'bg-emerald-500' : 'bg-white/15'}`}
                  >
                    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all
                                      ${depositEnabled ? 'left-[22px]' : 'left-0.5'}`} />
                  </button>
                </div>
                <label className="text-[11px] text-white/55 block mb-1">
                  Depósito mínimo sugerido (alz)
                </label>
                <input
                  type="number"
                  value={minDeposit}
                  onChange={(e) => setMinDeposit(Math.max(0, Number(e.target.value) || 0))}
                  className="w-full bg-[#0a1015] border border-white/[0.06] rounded-lg
                             px-3 py-2 text-sm text-white"
                />
              </div>
            </div>

            {/* Limites de venda */}
            <div className="mt-5 bg-[#0d141b] border border-white/[0.06] rounded-lg p-4">
              <p className="text-xs font-bold text-white/70 uppercase tracking-wider mb-1">
                Limites de venda por vendedor
              </p>
              <p className="text-[11px] text-white/45 mb-3">
                0 = sem limite. Ao atingir um limite, o vendedor some da lista de compra.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <LimitField label="Alz / dia" value={limits.limitAlzDay}
                            onChange={(v) => setLimits(l => ({ ...l, limitAlzDay: v }))} />
                <LimitField label="Alz / semana" value={limits.limitAlzWeek}
                            onChange={(v) => setLimits(l => ({ ...l, limitAlzWeek: v }))} />
                <LimitField label="Alz / mês" value={limits.limitAlzMonth}
                            onChange={(v) => setLimits(l => ({ ...l, limitAlzMonth: v }))} />
                <LimitField label="Vendas / dia" value={limits.limitSalesDay}
                            onChange={(v) => setLimits(l => ({ ...l, limitSalesDay: v }))} />
                <LimitField label="Vendas / semana" value={limits.limitSalesWeek}
                            onChange={(v) => setLimits(l => ({ ...l, limitSalesWeek: v }))} />
                <LimitField label="Vendas / mês" value={limits.limitSalesMonth}
                            onChange={(v) => setLimits(l => ({ ...l, limitSalesMonth: v }))} />
              </div>
            </div>

            {/* Venda de itens */}
            <div className="mt-5 bg-[#0d141b] border border-white/[0.06] rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-xs font-bold text-white/70 uppercase tracking-wider">
                    Venda de itens
                  </p>
                  <p className="text-[11px] text-white/45">
                    Liga o marketplace de itens (depósito + venda).
                  </p>
                </div>
                <button
                  onClick={() => setItemCfg(c => ({ ...c, itemsEnabled: !c.itemsEnabled }))}
                  className={`relative w-11 h-6 rounded-full transition shrink-0
                              ${itemCfg.itemsEnabled ? 'bg-emerald-500' : 'bg-white/15'}`}
                >
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all
                                    ${itemCfg.itemsEnabled ? 'left-[22px]' : 'left-0.5'}`} />
                </button>
              </div>
              <p className="text-[11px] text-white/45 mb-2">
                Preços globais em R$ — 0 = sem limite.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <LimitField label="Alz mín /100kk" value={itemCfg.alzPriceMinBrl}
                            onChange={(v) => setItemCfg(c => ({ ...c, alzPriceMinBrl: v }))} />
                <LimitField label="Alz máx /100kk" value={itemCfg.alzPriceMaxBrl}
                            onChange={(v) => setItemCfg(c => ({ ...c, alzPriceMaxBrl: v }))} />
                <LimitField label="Item mín" value={itemCfg.itemPriceMinBrl}
                            onChange={(v) => setItemCfg(c => ({ ...c, itemPriceMinBrl: v }))} />
                <LimitField label="Item máx" value={itemCfg.itemPriceMaxBrl}
                            onChange={(v) => setItemCfg(c => ({ ...c, itemPriceMaxBrl: v }))} />
              </div>
            </div>

            <button
              onClick={saveConfig}
              disabled={savingCfg}
              className="mt-4 px-6 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-400
                         text-[#0d141b] font-bold text-sm transition cursor-pointer
                         disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {savingCfg && <Loader2 size={14} className="animate-spin" />}
              Salvar configuração
            </button>
          </div>

          {/* Blacklist de itens */}
          <div className="surface-card rounded-xl p-5 mb-6">
            <div className="flex items-center gap-2 mb-1">
              <Ban size={15} className="text-red-400" />
              <p className="text-xs font-black uppercase tracking-[0.15em] text-white/55">
                Blacklist de itens
              </p>
            </div>
            <p className="text-[11px] text-white/45 mb-3">
              Itens proibidos no marketplace. Option vazia = qualquer option do item.
            </p>
            <div className="flex flex-wrap gap-2 mb-3">
              <input value={blItemId} onChange={(e) => setBlItemId(e.target.value)}
                     placeholder="ID do item" type="number"
                     className="w-32 bg-[#0d141b] border border-white/[0.06] rounded-lg
                                px-3 py-2 text-sm text-white" />
              <input value={blOption} onChange={(e) => setBlOption(e.target.value)}
                     placeholder="Option (opcional)" type="number"
                     className="w-40 bg-[#0d141b] border border-white/[0.06] rounded-lg
                                px-3 py-2 text-sm text-white" />
              <input value={blReason} onChange={(e) => setBlReason(e.target.value)}
                     placeholder="Motivo"
                     className="flex-1 min-w-[140px] bg-[#0d141b] border border-white/[0.06]
                                rounded-lg px-3 py-2 text-sm text-white" />
              <button onClick={addBl}
                      className="px-4 rounded-lg bg-red-500/15 hover:bg-red-500/25 text-red-400
                                 text-sm font-bold transition cursor-pointer">
                Bloquear
              </button>
            </div>
            {blacklist.length === 0 ? (
              <p className="text-xs text-white/35 py-2">Nenhum item bloqueado.</p>
            ) : (
              <div className="border border-white/[0.06] rounded-lg overflow-hidden">
                {blacklist.map(b => (
                  <div key={b.blacklistId}
                       className="flex items-center gap-3 px-3 py-2 border-b border-white/[0.04]
                                  last:border-0 text-sm">
                    <span className="font-mono text-white/80">#{b.itemId}</span>
                    <span className="text-[11px] text-white/45">
                      {b.itemOption != null ? `option ${b.itemOption}` : 'qualquer option'}
                    </span>
                    {b.reason && (
                      <span className="text-[11px] text-white/55 italic truncate">"{b.reason}"</span>
                    )}
                    <button onClick={() => delBl(b.blacklistId)}
                            className="ml-auto p-1.5 rounded bg-white/[0.04] hover:bg-red-500/20
                                       text-white/50 hover:text-red-400 cursor-pointer">
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Limites de preço por item */}
          <div className="surface-card rounded-xl p-5 mb-6">
            <div className="flex items-center gap-2 mb-1">
              <Tag size={15} className="text-amber-400" />
              <p className="text-xs font-black uppercase tracking-[0.15em] text-white/55">
                Limites de preço por item
              </p>
            </div>
            <p className="text-[11px] text-white/45 mb-3">
              Sobrepõe o limite global de item. 0 = sem teto. Salvar com um ID
              já cadastrado atualiza.
            </p>
            <div className="flex flex-wrap gap-2 mb-3">
              <input value={plItemId} onChange={(e) => setPlItemId(e.target.value)}
                     placeholder="ID do item" type="number"
                     className="w-32 bg-[#0d141b] border border-white/[0.06] rounded-lg
                                px-3 py-2 text-sm text-white" />
              <input value={plMin} onChange={(e) => setPlMin(e.target.value)}
                     placeholder="Mín R$" type="number"
                     className="w-28 bg-[#0d141b] border border-white/[0.06] rounded-lg
                                px-3 py-2 text-sm text-white" />
              <input value={plMax} onChange={(e) => setPlMax(e.target.value)}
                     placeholder="Máx R$" type="number"
                     className="w-28 bg-[#0d141b] border border-white/[0.06] rounded-lg
                                px-3 py-2 text-sm text-white" />
              <button onClick={savePl}
                      className="px-4 rounded-lg btn-primary
                                 text-sm font-bold transition cursor-pointer">
                Salvar
              </button>
            </div>
            {priceLimits.length === 0 ? (
              <p className="text-xs text-white/35 py-2">Nenhum limite por item.</p>
            ) : (
              <div className="border border-white/[0.06] rounded-lg overflow-hidden">
                {priceLimits.map(p => (
                  <div key={p.itemId}
                       className="flex items-center gap-3 px-3 py-2 border-b border-white/[0.04]
                                  last:border-0 text-sm">
                    <span className="font-mono text-white/80">#{p.itemId}</span>
                    <span className="text-[11px] text-white/55">
                      R$ {p.minPriceBrl.toFixed(2)} —{' '}
                      {p.maxPriceBrl > 0 ? `R$ ${p.maxPriceBrl.toFixed(2)}` : 'sem teto'}
                    </span>
                    <button onClick={() => delPl(p.itemId)}
                            className="ml-auto p-1.5 rounded bg-white/[0.04] hover:bg-red-500/20
                                       text-white/50 hover:text-red-400 cursor-pointer">
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Conceder vendedor */}
          <div className="surface-card rounded-xl p-5 mb-6">
            <p className="text-xs font-black uppercase tracking-[0.15em] text-white/55 mb-3">
              Habilitar conta como vendedor
            </p>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && doSearch()}
                  placeholder="Buscar por ID, email ou UserNum..."
                  className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg
                             pl-9 pr-3 py-2.5 text-sm text-white"
                />
              </div>
              <button
                onClick={doSearch}
                disabled={searching}
                className="px-5 rounded-lg bg-amber-500 hover:bg-amber-400
                           text-[#0d141b] font-bold text-sm transition cursor-pointer
                           disabled:opacity-50 flex items-center gap-2"
              >
                {searching && <Loader2 size={14} className="animate-spin" />}
                Buscar
              </button>
            </div>

            {searchResults.length > 0 && (
              <div className="mt-4 border border-white/[0.06] rounded-lg overflow-hidden">
                {searchResults.map(r => (
                  <div key={r.userNum}
                       className="flex items-center gap-3 px-4 py-2.5 border-b border-white/[0.04]
                                  last:border-0 hover:bg-white/[0.02]">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-white">{r.accountId}</p>
                      <p className="text-[11px] text-white/45 font-mono">
                        UserNum {r.userNum}{r.email ? ` · ${r.email}` : ''}
                      </p>
                    </div>
                    {r.isSeller ? (
                      <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400 font-bold">
                        JÁ É VENDEDOR
                      </span>
                    ) : (
                      <button
                        onClick={() => onGrant(r)}
                        className="px-3 py-1.5 rounded-lg bg-[#00e1d7]/10 hover:bg-[#00e1d7]/20
                                   text-[#00e1d7] text-xs font-bold transition cursor-pointer
                                   flex items-center gap-1"
                      >
                        <UserPlus size={12} />
                        Habilitar
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Lista de vendedores */}
          <div className="surface-card rounded-xl overflow-hidden data-tbl">
            <div className="px-5 py-3 bg-[#1a2531] border-b border-white/[0.06]">
              <p className="text-xs font-black uppercase tracking-[0.15em] text-white/55">
                Vendedores RMT
              </p>
            </div>
            {sellers.length === 0 ? (
              <div className="py-16 text-center text-white/40 text-sm">
                Nenhum vendedor cadastrado
              </div>
            ) : (
              sellers.map(s => (
                <div key={s.userNum}
                     className="flex items-start gap-4 px-5 py-4 border-b border-white/[0.04]
                                last:border-0 hover:bg-white/[0.02]">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-bold text-white">
                        {s.sellerName || s.accountId || `UserNum ${s.userNum}`}
                      </p>
                      <span className={`text-[10px] px-2 py-0.5 rounded font-black
                                        ${STATUS_BADGE[s.status] || 'bg-white/10 text-white/60'}`}>
                        {s.status === 'active' ? 'ATIVO' : 'SUSPENSO'}
                      </span>
                      <span className={`text-[10px] px-2 py-0.5 rounded font-bold
                                        ${DOC_BADGE[s.docStatus] || 'bg-white/10 text-white/60'}`}>
                        {DOC_LABEL[s.docStatus] || s.docStatus}
                      </span>
                    </div>
                    <p className="text-[11px] text-white/45 mt-1 font-mono">
                      {s.accountId || `UserNum ${s.userNum}`} · saldo escrow{' '}
                      <span className="text-amber-400 font-bold">{fmt(s.balanceAlz)} alz</span>
                    </p>
                    {s.pixKey && (
                      <p className="text-[11px] text-white/45 mt-0.5">
                        PIX ({s.pixKeyType || '—'}): {s.pixKey}
                      </p>
                    )}
                    {s.notes && (
                      <p className="text-[11px] text-white/65 mt-1 italic">"{s.notes}"</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditing(s)}
                      className="p-2 rounded-lg bg-white/[0.04] hover:bg-white/[0.08]
                                 text-white/65 hover:text-white cursor-pointer"
                      title="Editar"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => onRevoke(s)}
                      className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20
                                 text-red-400 cursor-pointer"
                      title="Revogar"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {cofreOpen && (
        <CofreModal
          onClose={() => setCofreOpen(false)}
          onSaved={async (cfg) => { setConfig(cfg); setCofreOpen(false); }}
        />
      )}
      {editing && (
        <SellerEditor
          seller={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { await fetchAll(); setEditing(null); }}
        />
      )}
    </div>
  );
}


// ----------------------------------------------------------------- Cofre modal

function CofreModal({
  onClose, onSaved,
}: {
  onClose: () => void;
  onSaved: (cfg: RmtConfig) => Promise<void>;
}) {
  const { notify } = useToast();
  const [term, setTerm] = useState('');
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<RmtCharSearch[]>([]);

  const search = async () => {
    if (term.trim().length < 2) { notify('error', 'Digite ao menos 2 caracteres'); return; }
    setBusy(true);
    try { setResults(await searchRmtChar(term.trim())); }
    catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
    finally { setBusy(false); }
  };

  const pick = async (c: RmtCharSearch) => {
    try {
      const cfg = await setRmtCofre(c.characterIdx);
      notify('success', `Char-cofre definido: ${c.name}`);
      await onSaved(cfg);
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="surface-card rounded-2xl p-6 max-w-md w-full"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white">Definir char-cofre</h3>
          <button onClick={onClose} className="text-white/55 hover:text-white">
            <X size={18} />
          </button>
        </div>
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && search()}
            placeholder="Nome do personagem ou CharacterIdx"
            className="flex-1 bg-[#0d141b] border border-white/[0.06] rounded-lg
                       px-3 py-2 text-sm text-white"
          />
          <button
            onClick={search}
            disabled={busy}
            className="px-4 rounded-lg btn-primary
                       font-bold text-sm cursor-pointer disabled:opacity-50"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : 'Buscar'}
          </button>
        </div>
        <div className="max-h-72 overflow-y-auto space-y-1">
          {results.map(c => (
            <button
              key={c.characterIdx}
              onClick={() => pick(c)}
              className="w-full text-left px-3 py-2 rounded-lg bg-white/[0.03]
                         hover:bg-amber-500/10 border border-white/[0.06]
                         hover:border-amber-400/40 transition cursor-pointer"
            >
              <span className="text-sm font-bold text-white">{c.name}</span>
              <span className="text-[11px] text-white/45 font-mono">
                {' '}· idx {c.characterIdx} · conta {c.userNum}
                {c.online && <span className="text-emerald-400"> · online</span>}
              </span>
            </button>
          ))}
          {!busy && results.length === 0 && (
            <p className="text-xs text-white/40 text-center py-4">
              Busque um personagem para defini-lo como cofre.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}


// ----------------------------------------------------------------- Seller editor

function SellerEditor({
  seller, onClose, onSaved,
}: {
  seller: RmtSeller;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { notify } = useToast();
  const [status, setStatus] = useState(seller.status);
  const [docStatus, setDocStatus] = useState(seller.docStatus);
  const [notes, setNotes] = useState(seller.notes || '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await updateRmtSeller(seller.userNum, { status, docStatus, notes: notes || undefined });
      notify('success', 'Vendedor atualizado');
      await onSaved();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    } finally {
      setBusy(false);
    }
  };

  const pill = (active: boolean) =>
    `flex-1 px-3 py-2 rounded-lg text-xs font-bold transition cursor-pointer border ${
      active ? 'bg-amber-500/15 border-amber-400/40 text-amber-400'
             : 'bg-white/[0.03] border-white/[0.06] text-white/65 hover:bg-white/[0.06]'}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="surface-card rounded-2xl p-6 max-w-md w-full"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-lg font-bold text-white">Editar vendedor</h3>
            <p className="text-[11px] text-white/55 font-mono mt-1">
              {seller.accountId} · UserNum {seller.userNum}
            </p>
          </div>
          <button onClick={onClose} className="text-white/55 hover:text-white">
            <X size={18} />
          </button>
        </div>

        <label className="text-[11px] text-white/55 mb-1.5 block">Status</label>
        <div className="flex gap-2 mb-4">
          <button className={pill(status === 'active')} onClick={() => setStatus('active')}>
            Ativo
          </button>
          <button className={pill(status === 'suspended')} onClick={() => setStatus('suspended')}>
            Suspenso
          </button>
        </div>

        <label className="text-[11px] text-white/55 mb-1.5 block">Documentação</label>
        <div className="flex gap-2 mb-4">
          <button className={pill(docStatus === 'pending')} onClick={() => setDocStatus('pending')}>
            Pendente
          </button>
          <button className={pill(docStatus === 'approved')} onClick={() => setDocStatus('approved')}>
            Aprovada
          </button>
          <button className={pill(docStatus === 'rejected')} onClick={() => setDocStatus('rejected')}>
            Rejeitada
          </button>
        </div>

        <label className="text-[11px] text-white/55 mb-1 block">Notas (opcional)</label>
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={255}
          className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg
                     px-3 py-2 text-sm text-white mb-5"
        />

        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-lg bg-white/[0.04] text-white/75
                       hover:bg-white/[0.08] transition cursor-pointer text-sm"
          >
            Cancelar
          </button>
          <button
            onClick={save}
            disabled={busy}
            className="flex-1 px-4 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-400
                       text-[#0d141b] font-bold text-sm transition cursor-pointer
                       disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}
